import { useCallback, useEffect, useState } from 'react';
import type { AutomationCreateRule, AutomationRule, AutomationRun, AutomationTriggerEvent } from '@raptor/contracts';
import { ApiResponseError, apiClient } from '../lib/api-client.js';

type Assignee = { membershipId: string; displayName: string | null; role: string };
type ActionType = AutomationCreateRule['actionType'];

const triggerOptions = [
  'crm.customer.created', 'crm.customer.updated', 'crm.contact.updated',
  'crm.lead.created', 'crm.lead.status_changed', 'crm.lead.assigned',
  'crm.lead.source_changed', 'crm.task.created', 'crm.task.completed',
  'crm.task.assigned', 'crm.task.reopened',
] as const;

export function AutomationWorkspace({
  organizationId, branchId, assignees, canConfigure,
}: {
  organizationId: string;
  branchId: string;
  assignees: Assignee[];
  canConfigure: boolean;
}) {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [triggerEvents, setTriggerEvents] = useState<AutomationTriggerEvent[]>([]);
  const [name, setName] = useState('Create a prompt follow-up');
  const [triggerEventType, setTriggerEventType] = useState<(typeof triggerOptions)[number]>('crm.customer.created');
  const [actionType, setActionType] = useState<ActionType>('CREATE_CRM_FOLLOW_UP');
  const [title, setTitle] = useState('Review this customer activity');
  const [assigneeMembershipId, setAssigneeMembershipId] = useState('');
  const [dueInMinutes, setDueInMinutes] = useState('30');
  const [severity, setSeverity] = useState<'INFO' | 'WARNING' | 'CRITICAL'>('WARNING');
  const [metadataKey, setMetadataKey] = useState('');
  const [metadataValue, setMetadataValue] = useState('');
  const [executionEventByRule, setExecutionEventByRule] = useState<Record<string, string>>({});
  const [triggerEventsUnavailable, setTriggerEventsUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAssigneeMembershipId((current) => assignees.some((assignee) => assignee.membershipId === current) ? current : assignees[0]?.membershipId ?? '');
  }, [assignees]);

  const load = useCallback(async () => {
    setError(null);
    const coreLoad = Promise.allSettled([
      apiClient.getAutomationRules(organizationId),
      apiClient.getAutomationRuns(organizationId),
    ]).then(([rulesResult, runsResult]) => {
      if (rulesResult.status === 'fulfilled') setRules(rulesResult.value); else setRules([]);
      if (runsResult.status === 'fulfilled') setRuns(runsResult.value); else setRuns([]);
      const coreFailure = rulesResult.status === 'rejected' ? rulesResult.reason : runsResult.status === 'rejected' ? runsResult.reason : null;
      if (coreFailure) {
        setError(coreFailure instanceof ApiResponseError && coreFailure.status === 403
          ? 'Automation history cannot be accessed for this workspace role.'
          : 'Automation data is temporarily unavailable.');
      }
    });
    const triggerEventsLoad = apiClient.getAutomationTriggerEvents(organizationId).then((events) => {
      setTriggerEvents(events);
      setTriggerEventsUnavailable(false);
    }).catch(() => {
      setTriggerEvents([]);
      setExecutionEventByRule({});
      setTriggerEventsUnavailable(true);
    });
    await Promise.all([coreLoad, triggerEventsLoad]);
  }, [organizationId]);

  useEffect(() => { void load(); }, [load]);

  const createRule = async () => {
    if (!canConfigure) return;
    const minutes = Number(dueInMinutes);
    const hasCondition = Boolean(metadataKey.trim());
    if (!name.trim() || !title.trim() || hasCondition !== Boolean(metadataValue.trim()) || (actionType === 'CREATE_CRM_FOLLOW_UP' && (!assigneeMembershipId || !Number.isInteger(minutes) || minutes < 0 || minutes > 10_080))) {
      setError('Enter a valid rule name, internal action title, and follow-up assignment. A condition needs both a metadata key and expected value.');
      return;
    }
    const conditions = hasCondition ? { metadataContains: { [metadataKey.trim()]: metadataValue.trim() } } : {};
    const base = { organizationId, branchId, name: name.trim(), triggerEventType, conditions, maxAttempts: 3 } as const;
    const input: AutomationCreateRule = actionType === 'CREATE_CRM_FOLLOW_UP'
      ? { ...base, actionType, actionConfig: { title: title.trim(), assigneeMembershipId, dueInMinutes: minutes } }
      : { ...base, actionType, actionConfig: { title: title.trim(), severity } };
    setBusy(true); setError(null);
    try {
      await apiClient.createAutomationRule(input);
      await load();
    } catch {
      setError('The automation rule could not be saved. Only authorized internal actions are accepted.');
    } finally { setBusy(false); }
  };

  const executeRule = async (rule: AutomationRule) => {
    const triggerTimelineEventId = executionEventByRule[rule.id];
    if (!triggerTimelineEventId) {
      setError('Choose matching customer activity before running this rule.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await apiClient.executeAutomationRule(rule.id, triggerTimelineEventId);
      await load();
    } catch {
      setError('The automation could not run for that activity. Confirm the event still matches this rule.');
    } finally { setBusy(false); }
  };

  const matchingEvents = (rule: AutomationRule) => triggerEvents.filter((event) => event.branchId === rule.branchId && event.eventType === rule.triggerEventType);

  return <section className="workspace-rail" aria-labelledby="automation-title">
    <div className="rail-heading"><div><p className="eyebrow">Automation</p><h2 id="automation-title">Internal rules and run history</h2></div><button className="text-action" type="button" onClick={() => void load()}>Refresh</button></div>
    <p className="context-card__meta">Rules can create only an authorized CRM follow-up or an internal operational alert. No message is sent to WhatsApp, email, Meta, or any provider.</p>
    {canConfigure ? <form className="crm-form" onSubmit={(event) => { event.preventDefault(); void createRule(); }}>
      <label>Rule name<input required maxLength={160} value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label>Persisted CRM trigger<select aria-label="Automation trigger" value={triggerEventType} onChange={(event) => setTriggerEventType(event.target.value as (typeof triggerOptions)[number])}>{triggerOptions.map((trigger) => <option key={trigger} value={trigger}>{trigger.replaceAll('.', ' ')}</option>)}</select></label>
      <label>Internal action<select aria-label="Automation action" value={actionType} onChange={(event) => setActionType(event.target.value as ActionType)}><option value="CREATE_CRM_FOLLOW_UP">Create CRM follow-up</option><option value="CREATE_INTERNAL_OPERATIONAL_ALERT">Create operational alert</option></select></label>
      <label>Match event metadata key (optional)<input aria-label="Automation metadata key" maxLength={120} value={metadataKey} onChange={(event) => setMetadataKey(event.target.value)} /></label>
      <label>Expected metadata value (optional)<input aria-label="Automation metadata value" maxLength={240} value={metadataValue} onChange={(event) => setMetadataValue(event.target.value)} /></label>
      <label>Internal title<input required maxLength={240} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
      {actionType === 'CREATE_CRM_FOLLOW_UP' ? <>
        <label>Assignee<select aria-label="Automation assignee" required value={assigneeMembershipId} onChange={(event) => setAssigneeMembershipId(event.target.value)}><option value="">Select an eligible assignee</option>{assignees.map((assignee) => <option key={assignee.membershipId} value={assignee.membershipId}>{assignee.displayName ?? 'Verified member'} · {assignee.role}</option>)}</select></label>
        <label>Due in minutes<input aria-label="Automation due in minutes" inputMode="numeric" min="0" max="10080" value={dueInMinutes} onChange={(event) => setDueInMinutes(event.target.value)} /></label>
      </> : <label>Alert severity<select aria-label="Automation alert severity" value={severity} onChange={(event) => setSeverity(event.target.value as typeof severity)}><option value="INFO">Info</option><option value="WARNING">Warning</option><option value="CRITICAL">Critical</option></select></label>}
      <button className="secondary-action" disabled={busy} type="submit">Create internal rule</button>
    </form> : <p className="context-card__meta">Only an owner or tenant administrator can configure automation rules. You can still review the branch-scoped history.</p>}
    {error ? <p role="alert">{error}</p> : null}
    <h3>Rules</h3>
    {rules.length ? <ul className="crm-list">{rules.map((rule) => {
      const events = matchingEvents(rule);
      const selectedEventId = executionEventByRule[rule.id] ?? '';
      const condition = rule.conditions.metadataContains
        ? Object.entries(rule.conditions.metadataContains).map(([key, value]) => `${key} = ${String(value)}`).join(', ')
        : 'No metadata condition';
      return <li key={rule.id}>
        <strong>{rule.name}</strong>
        <span>{rule.triggerEventType} → {rule.actionType === 'CREATE_CRM_FOLLOW_UP' ? 'CRM follow-up' : 'Internal operational alert'}</span>
        <span>Condition: {condition}</span>
        <span>{rule.isEnabled ? 'Enabled' : 'Disabled'} · max {rule.maxAttempts} attempts</span>
        {canConfigure ? <button className="text-action" type="button" onClick={() => void apiClient.setAutomationRuleEnabled(rule.id, !rule.isEnabled).then(load).catch(() => setError('Rule state could not be updated.'))}>{rule.isEnabled ? 'Disable' : 'Enable'}</button> : null}
        {rule.isEnabled ? <>
          <label>Matching activity<select aria-label={`Matching activity for ${rule.name}`} value={selectedEventId} onChange={(event) => setExecutionEventByRule((current) => ({ ...current, [rule.id]: event.target.value }))}><option value="">Select matching activity</option>{events.map((event) => <option key={event.id} value={event.id}>{event.eventType.replaceAll('.', ' ')} · {new Date(event.occurredAt).toLocaleString()}</option>)}</select></label>
          <button className="text-action" type="button" disabled={busy || !selectedEventId} onClick={() => void executeRule(rule)}>Run internal action</button>
          {triggerEventsUnavailable ? <span>Matching activities could not be loaded. Refresh to retry.</span> : !events.length ? <span>No matching persisted activity exists.</span> : null}
        </> : null}
      </li>;
    })}</ul> : <p>No internal automation rules exist for this authorized scope.</p>}
    <h3>Run history</h3>
    {runs.length ? <ol className="crm-list">{runs.map((run) => <li key={run.id}><strong>{run.status.replaceAll('_', ' ')}</strong><span>{run.actionType === 'CREATE_CRM_FOLLOW_UP' ? 'CRM follow-up created' : run.actionType === 'CREATE_INTERNAL_OPERATIONAL_ALERT' ? 'Internal alert created' : 'No action result'} · attempt {run.attemptCount}/{run.maxAttempts}</span><span>{new Date(run.createdAt).toLocaleString()}</span>{run.status === 'RETRYABLE_FAILURE' ? <button className="text-action" type="button" onClick={() => void apiClient.retryAutomationRun(run.id).then(load).catch(() => setError('Run retry was denied or failed.'))}>Retry internal run</button> : null}</li>)}</ol> : <p>No attributable automation runs exist yet.</p>}
  </section>;
}
